import { useRouter } from "next/router";
import React, { useState } from "react";
import { Popover } from "react-tiny-popover";

import PartPendingActionWrapper from "@/PartPendingActionWrapper";

import styles from "./PartDropDownMenu.module.scss";

type ChildElement =
  | React.ReactElement
  | HTMLElement
  | HTMLButtonElement
  | HTMLLinkElement;

type onClickT = (event?: React.MouseEvent<ChildElement>) => Promise<void> | void;

interface PartDropDownMenuProps {
  menu: {
    title: string;
    href?: string;
    onClick?: onClickT;
    extraAttributes?: { [k: string]: string };
  }[];
  children: ChildElement;
  className?: string;
}

const PartDropDownMenu: React.FC<PartDropDownMenuProps> = ({ menu, children }) => {
  const child = children as React.ReactElement<{
    onClick?: onClickT;
    ref: React.RefObject<ChildElement>;
  }>;

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const router = useRouter();

  const contentFn = React.useCallback(
    () => (
      <div className={styles.menu} data-testid="popover">
        {menu?.map((item, index) => {
          const Tag = item.href ? "a" : "button";
          return (
            <PartPendingActionWrapper
              block
              onClick={async e => {
                e?.preventDefault();
                if (item.onClick) await item.onClick(e);
                if (item.href) await router.push(item.href);
                setIsPopoverOpen(false);
              }}
              key={item.title + index}
            >
              <Tag
                {...(item.href ? { href: item.href } : {})}
                className={styles.menuItem}
                {...item.extraAttributes}
              >
                {item.title}
              </Tag>
            </PartPendingActionWrapper>
          );
        })}
      </div>
    ),
    [menu],
  );

  if (!React.isValidElement(children)) return null;

  return (
    <Popover
      isOpen={isPopoverOpen}
      positions={["bottom", "right", "top"]}
      align={"end"}
      padding={10}
      reposition
      containerStyle={{ zIndex: "9999" }}
      onClickOutside={() => setIsPopoverOpen(false)} // handle click events outside of the popover/target here!
      content={contentFn}
    >
      {React.cloneElement(child, {
        onClick: () => setIsPopoverOpen(prev => !prev),
        // @ts-expect-error No definition
        "data-test-wrapped-by-popover": "",
        "data-test-drop-down-button": "",
      })}
    </Popover>
  );
};

export default PartDropDownMenu;
